import { useCallback, useEffect, useMemo, useState } from "react";

import {
  inputValidator,
  isValidEmail,
  isValidMobile,
  isValidName,
  matchCountryRegex,
} from "@/utils";

import { useAppDispatch, useAppSelector } from "@/hooks";
import * as actions from "@/redux/LandingPageReducer";

const useRegisterForm = (fields) => {
  const dispatch = useAppDispatch();

  const {
    promoCodeSuccess: isPromoCodeApplied,
    isPromoCodeLoading: isPromoCodeChecking,
  } = useAppSelector((state) => state.landingPage);
  const { promoCodeData } = useAppSelector((state) => state.landingPage);

  const [initialState, setInitialState] = useState({});
  const [initialErrors, setInitialErrors] = useState({});
  const [fieldKeys, setFieldKeys] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState({});
  const [promoCodeButtonText, setPromoCodeButtonText] = useState("APPLY");

  useEffect(() => {
    const _initialInputs = {};
    const _initialErrors = {};

    const _fieldKeys = Object.keys(fields) || [];
    setFieldKeys(_fieldKeys);

    _fieldKeys?.forEach((key) => {
      _initialInputs[key] = fields[key];
      _initialErrors[`${key}Error`] = "";
    });

    setInitialErrors(_initialErrors);

    const _initialState = { ..._initialInputs, ..._initialErrors };
    setInitialState(_initialState);
    setInputs(_initialState);
    setPromoCodeButtonText(_initialState?.promoCodeButtonText || "APPLY");
  }, [fields]);

  const { countryCode, mobile, email, selectedPackage, promoCode } = inputs;

  const applyCode = useCallback(
    (apiData) => dispatch(actions.promoCodeCheckRequest(apiData)),
    [dispatch]
  );
  const removeCode = useCallback(
    () => dispatch(actions.removePromoCode()),
    [dispatch]
  );

  const applyPromoCodeHelper = useCallback(
    (isValidInputs, apiData) => {
      if (isValidInputs) {
        applyCode(apiData).then((res) => {
          const error = res?.payload?.error || !res?.payload?.status;
          if (error) {
            setInputs((prevState) => ({
              ...prevState,
              promoCodeError: res?.payload?.message,
            }));
          } else {
            setPromoCodeButtonText("REMOVE");
          }
        });
      }
    },
    [applyCode]
  );

  const applyPromoCode = useCallback(
    (isValidInputs, apiData) => {
      setInputs((prevState) => ({ ...prevState, promoCodeError: "" }));
      if (!promoCode) {
        setInputs((prevState) => ({
          ...prevState,
          promoCodeError: "Please enter promo code",
        }));
        return;
      }
      applyPromoCodeHelper(isValidInputs, apiData);
    },
    [applyPromoCodeHelper, promoCode]
  );

  const removeAppliedPromoCode = useCallback(() => {
    setInputs((prevState) => ({
      ...prevState,
      promoCode: "",
      promoCodeError: "",
    }));
    setPromoCodeButtonText("APPLY");
    removeCode();
  }, [removeCode]);

  useEffect(() => {
    return () => removeAppliedPromoCode();
  }, [removeAppliedPromoCode]);

  const removePromoCodeOnGoalPackageChange = useCallback(() => {
    if (promoCode) {
      removeAppliedPromoCode();
      setInputs((prevState) => ({
        ...prevState,
        promoCodeError: `Promo code has removed, your promo code was "${promoCode}". Please apply promo code again.`,
      }));
    }
  }, [promoCode, removeAppliedPromoCode]);

  const removeSelectedPackageOnGoalChange = useCallback(
    (name) => {
      if (selectedPackage && name === "goal")
        setInputs((prevState) => ({ ...prevState, selectedPackage: "" }));
    },
    [selectedPackage]
  );

  const handleBlur = useCallback(
    (event, onBlur) => {
      const field = event?.target?.name;
      const value = event?.target?.value;

      const isValidContactDetail =
        (field === "mobile" &&
          isValidMobile(value) &&
          matchCountryRegex(countryCode, value)) ||
        (field === "email" && isValidEmail(value)) ||
        (isValidMobile(mobile) && matchCountryRegex(countryCode, mobile)) ||
        isValidEmail(email);

      if (isValidContactDetail) onBlur(event);
    },
    [countryCode, mobile, email]
  );

  const handleChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      if (name === "promoCode") {
        removeAppliedPromoCode();
        setPromoCodeButtonText("APPLY");
      }
      if (fieldKeys.includes(name)) {
        if (name === "name") {
          if (value === "" || isValidName(value))
            setInputs((inputs) => ({ ...inputs, [name]: value }));
        } else if (name === "mobile") {
          if (value === "" || isValidMobile(value))
            setInputs((inputs) => ({ ...inputs, [name]: value }));
        } else {
          setInputs((inputs) => ({ ...inputs, [name]: value }));
        }
        setInputs((inputs) => ({ ...inputs, [name + "Error"]: "" }));
      }
    },
    [fieldKeys, removeAppliedPromoCode]
  );

  const handleSelectChange = useCallback(
    (name, value) => {
      removePromoCodeOnGoalPackageChange();
      removeSelectedPackageOnGoalChange(name);

      if (fieldKeys.includes(name)) {
        setInputs((inputs) => ({
          ...inputs,
          [name]: value,
          [name + "Error"]: "",
        }));
      }
    },
    [
      fieldKeys,
      removePromoCodeOnGoalPackageChange,
      removeSelectedPackageOnGoalChange,
    ]
  );

  const handleCheckBoxChange = useCallback(
    (event) => {
      const { name, checked } = event.target;
      if (fieldKeys.includes(name)) {
        setInputs((inputs) => ({
          ...inputs,
          [name]: checked,
          [name + "Error"]: "",
        }));
      }
    },
    [fieldKeys]
  );

  const resetErrors = useCallback(() => {
    setInputs((prevState) => ({
      ...prevState,
      ...initialErrors,
    }));
  }, [initialErrors]);

  const validateInput = useCallback(
    (values) => {
      resetErrors();

      const { errors, isError } = inputValidator({ ...values });

      if (isError) {
        setInputs((inputs) => ({
          ...inputs,
          ...errors,
        }));
        setIsLoading(false);
        return false;
      } else {
        setIsLoading(true);
        return true;
      }
    },
    [resetErrors]
  );

  const onSubmitCallback = useCallback(
    (values, onSubmit) => {
      if (validateInput(values)) {
        onSubmit();
      }
    },
    [validateInput]
  );

  const onShowCallbackModal = useCallback((onHide) => {
    setIsFormSubmitted(true);
    setShowSuccessModal(true);
  }, []);

  const onHideCallbackModal = useCallback(
    (onHide) => {
      onHide();
      setInputs(initialState);
      setShowSuccessModal(false);
    },
    [initialState]
  );

  return {
    inputs,
    isFormSubmitted,
    showSuccessModal,
    isLoading,
    handleBlur,
    handleChange,
    handleSelectChange,
    onSubmitCallback,
    onShowCallbackModal,
    onHideCallbackModal,
    handleCheckBoxChange,
    validateInput,
    promoCodeButtonText,
    applyPromoCode,
    isPromoCodeApplied,
    isPromoCodeChecking,
    promoCodeData,
    removeAppliedPromoCode,
  };
};

export default useRegisterForm;
